import { GitFork } from 'lucide-react';
import type { useTranslation } from 'react-i18next';

import type { SidebarGroup, SidebarItem } from '@/components/shell/Sidebar';

import {
  FAMILY_RECORD_SECTIONS,
  FAMILY_SECTION_GROUP_ORDER,
  type FamilySectionGroup,
} from '../sections.config';

type Translator = ReturnType<typeof useTranslation>['t'];

const GROUP_HEADING_KEY: Record<FamilySectionGroup, string> = {
  people: 'routes.family.sidebar.peopleGroup',
  files: 'routes.family.sidebar.filesGroup',
};

/**
 * Sidebar groups for the family record route. Mirrors `patientSidebarGroups`
 * — buckets registry entries into ordered groups, marks the first section
 * as `defaultActive` so the highlight stays correct before the user picks
 * a section.
 */
export function familySidebarGroups(
  guid: string,
  t: Translator,
): readonly SidebarGroup[] {
  const base = `/families/${guid}`;
  const defaultSectionId = FAMILY_RECORD_SECTIONS[0]?.id;

  return FAMILY_SECTION_GROUP_ORDER.map<SidebarGroup>((group) => {
    const items = FAMILY_RECORD_SECTIONS.filter(
      (section) => section.group === group,
    ).map<SidebarItem>((section) => ({
      key: section.id,
      label: t(section.labelKey),
      to: `${base}#${section.id}`,
      icon: <section.Icon className="h-4 w-4" aria-hidden="true" />,
      ...(section.id === defaultSectionId ? { defaultActive: true } : {}),
    }));

    // The People group gets the Pedigree sibling-route entry (PTV2-1157): a deep link to the
    // embedded pedigree editor mounted at `families/:guid/pedigree`.
    if (group === 'people') {
      items.push({
        key: 'pedigree',
        label: t('routes.family.sidebar.pedigree', { defaultValue: 'Pedigree' }),
        to: `${base}/pedigree`,
        icon: <GitFork className="h-4 w-4" aria-hidden="true" />,
      });
    }

    return {
      key: group,
      heading: t(GROUP_HEADING_KEY[group]),
      items,
    };
  });
}
